
import {
  computed,
  defineComponent,
  h,
  PropType,
  ref,
  toRefs
} from '~/utils/nuxt3-migration'
import { useVNode } from '~/compositions/vnode'
import { VNode } from 'vue'
import CInputGroupText from '~/components/shared/configurable/form/input/CInputGroupText.vue'
import { useFormComponent } from '~/compositions/form-component'
import CIcon from '~/components/shared/configurable/CIcon.vue'
import { useId } from '~/compositions/id'
import { InputSize } from '~/models/app/input'

export default defineComponent({
  props: {
    size: {
      type: String as PropType<InputSize>,
      default: 'md',
      required: false
    },
    prepend: {
      type: String,
      default: ''
    },
    append: {
      type: String,
      default: ''
    },
    inputClass: {
      type: [Object, Array, String],
      default: () => {
        return []
      }
    },
    inputRoundnessClass: {
      type: [Object, Array, String],
      default: () => {
        return []
      }
    },
    hasError: {
      type: Boolean,
      default: false
    },
    success: {
      type: Boolean,
      default: false
    },
    warning: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    helperText: {
      type: String,
      default: null
    },
    insetButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    id: {
      type: String,
      default: null
    },
    wrapperClass: {
      type: [Object, Array, String],
      default: () => {
        return []
      }
    }
  },
  setup(props, { slots, emit }) {
    const {
      size,
      prepend,
      append,
      hasError,
      success,
      warning,
      disabled,
      helperText,
      insetButtons,
      inputClass,
      inputRoundnessClass,
      id,
      wrapperClass
    } = toRefs(props)

    const { addPropertiesToVNodes, addPropertiesToVNode } = useVNode()

    const focused = ref(false)

    const { uuid } = useId()

    const defaultSlotComponent = computed(() => {
      const defaultSlots = slots.default()
      if (defaultSlots?.length) {
        const mainComponent = defaultSlots.find(s => s.componentOptions)
        return mainComponent
      }

      return undefined
    })
    const idToUse = computed(() => {
      if (defaultSlotComponent.value?.componentOptions?.propsData?.id) {
        return defaultSlotComponent.value.componentOptions.propsData.id
      }

      if (id.value) {
        return id.value
      }

      return uuid()
    })

    const disabledToUse = computed(() => {
      return disabled.value
    })

    const { helperTextClasses, helperTextIcon } = useFormComponent(
      ref(''),
      hasError,
      success,
      warning,
      'input-group'
    )

    const hasPrependedContent = computed(() => {
      if (slots.prepend) {
        const nodes = slots.prepend()
        return Boolean(nodes?.length)
      }

      return Boolean(prepend.value)
    })
    const hasAppendedContent = computed(() => {
      if (slots.append) {
        const nodes = slots.append()
        return Boolean(nodes?.length)
      }

      return Boolean(append.value)
    })

    const inputClasses = computed(() => {
      const classes = []
      if (
        !inputRoundnessClass.value ||
        inputRoundnessClass.value?.length === 0
      ) {
        if (hasPrependedContent.value && hasAppendedContent.value) {
          classes.push('!tw-rounded-none')
        }
        if (hasPrependedContent.value) {
          classes.push('!tw-rounded-l-none')
        }
        if (hasAppendedContent.value) {
          classes.push('!tw-rounded-r-none')
        }
      }

      if (size.value === 'lg') {
        classes.push(defaultSlotIsSelect.value ? '!tw-pl-3' : '!tw-px-3')
      } else {
        classes.push(defaultSlotIsSelect.value ? '!tw-pl-2' : '!tw-px-2')
      }

      return [
        inputClass.value,
        inputRoundnessClass.value,
        ...classes,
        '!tw-outline-none focus:!tw-ring-transparent !tw-border-transparent'
      ]
    })

    function vNodeIsButton(vNode: VNode): boolean {
      return vNode.componentOptions?.tag === 'CButton'
    }
    function vNodeIsInputGroupText(vNode: VNode): boolean {
      return vNode.tag?.endsWith('CInputGroupText')
    }
    function vNodeIsSelect(vNode: VNode): boolean {
      return ['CFormSelect', 'CCustomSelect'].includes(
        vNode.componentOptions?.tag
      )
    }

    function renderContentBeforeAndAfter(
      vNodes: VNode[],
      placement: 'append' | 'prepend'
    ): VNode {
      if (!vNodes || vNodes.length === 0) {
        return
      }
      const [node] = vNodes
      const isPrepended = placement === 'prepend'
      let roundnessClass
      if (isPrepended) {
        switch (size.value) {
          case 'sm': {
            roundnessClass = 'tw-rounded-l-md'
            break
          }
          case 'lg': {
            roundnessClass = 'tw-rounded-l-xl'
            break
          }
          case 'md':
          default: {
            roundnessClass = 'tw-rounded-l-lg'
            break
          }
        }
      } else {
        switch (size.value) {
          case 'sm': {
            roundnessClass = 'tw-rounded-r-md'
            break
          }
          case 'lg': {
            roundnessClass = 'tw-rounded-r-xl'
            break
          }
          case 'md':
          default: {
            roundnessClass = 'tw-rounded-r-lg'
            break
          }
        }
      }

      if (vNodeIsButton(node)) {
        let staticClasses

        if (!insetButtons.value) {
          staticClasses = isPrepended
            ? 'tw-rounded-r-none'
            : 'tw-rounded-l-none'
        } else {
          staticClasses = size.value === 'sm' ? 'tw-mr-1' : 'tw-mr-2'
        }

        let sizeToUse = size.value

        if (insetButtons.value) {
          switch (size.value) {
            case 'lg': {
              sizeToUse = 'sm'
              break
            }
            case 'sm':
            case 'md':
            default: {
              sizeToUse = 'xs'
              break
            }
          }
        }
        return h(
          'div',
          { class: { 'tw-self-center': insetButtons.value } },
          addPropertiesToVNodes(vNodes, {
            props: { size: sizeToUse, inGroup: true },
            staticClasses,
            disabled: disabledToUse.value
          })
        )
      } else if (vNodeIsSelect(node)) {
        let borderClasses

        if (isPrepended) {
          borderClasses = 'tw-border tw-border-r-solid !tw-rounded-r-none '
        } else {
          borderClasses =
            'tw-border tw-border-l-solid !tw-rounded-l-none !tw-border-grey-300'
        }

        if (disabledToUse.value) {
          borderClasses += ' !tw-border-grey-400'
        } else {
          borderClasses += ' !tw-border-grey-300'
        }

        const propsToUse = {
          size: size.value,
          roundnessClass,
          hasError: hasError.value,
          success: success.value,
          warning: warning.value,
          disabled: disabledToUse.value,
          inGroup: true
        }

        if (node.componentOptions?.tag === 'CCustomSelect') {
          propsToUse.headerClasses = `!tw-outline-none !tw-ring-transparent focus:!tw-ring-transparent tw-select-none ${borderClasses}`
        } else {
          propsToUse.selectClass = `!tw-outline-none focus:!tw-ring-transparent tw-select-none ${borderClasses}`
        }
        return h(
          'div',
          { staticClass: 'tw-inline-flex' },
          addPropertiesToVNodes(vNodes, {
            props: propsToUse
          })
        )
      } else if (vNodeIsInputGroupText(node)) {
        return h(
          'div',
          { staticClass: 'tw-inline-flex' },
          addPropertiesToVNodes(vNodes, {
            props: {
              size: size.value,
              hasError: hasError.value,
              success: success.value,
              warning: warning.value,
              disabled: disabledToUse.value,
              isPrepended,
              forId: idToUse.value
            }
          })
        )
      }
      return h(
        'div',
        { staticClass: 'tw-inline-flex' },
        addPropertiesToVNodes(vNodes, {
          props: {
            size: size.value,
            roundnessClass,
            isPrepended,
            hasError: hasError.value,
            success: success.value,
            warning: warning.value,
            disabled: disabledToUse.value
          },
          staticClasses: isPrepended ? '-tw-mr-px' : '-tw-ml-px'
        })
      )
    }

    const defaultSlotIsSelect = computed(() => {
      return defaultSlotComponent.value
        ? vNodeIsSelect(defaultSlotComponent.value)
        : false
    })

    function renderContent(): VNode[] {
      const r = []
      if (slots.prepend) {
        r.push(renderContentBeforeAndAfter(slots.prepend(), 'prepend'))
      } else if (prepend.value) {
        r.push(
          renderContentBeforeAndAfter(
            [h(CInputGroupText, prepend.value)],
            'prepend'
          )
        )
      }
      if (slots.default) {
        const propsToUse = {
          size: size.value,
          hasError: hasError.value,
          success: success.value,
          warning: warning.value,
          disabled: disabledToUse.value,
          id: idToUse.value,
          inGroup: true
        }

        if (defaultSlotIsSelect.value) {
          propsToUse.selectClass = inputClasses.value
        } else {
          propsToUse.inputClass = inputClasses.value
        }
        r.push(
          h(
            'div',
            {
              staticClass: 'tw-flex-1'
            },
            addPropertiesToVNodes(slots.default(), {
              props: propsToUse,
              listeners: {
                focus: e => {
                  focused.value = true
                  emit('focus', e)
                },
                blur: e => {
                  focused.value = false
                  emit('blur', e)
                }
              }
            })
          )
        )
      }
      if (slots.append) {
        r.push(renderContentBeforeAndAfter(slots.append(), 'append'))
      } else if (append.value) {
        r.push(
          renderContentBeforeAndAfter(
            [h(CInputGroupText, append.value)],
            'append'
          )
        )
      }
      return r
    }

    const wrapperRoundnessClass = computed(() => {
      switch (size.value) {
        case 'sm': {
          return 'tw-rounded-md'
        }
        case 'lg': {
          return 'tw-rounded-xl'
        }
        case 'md':
        default: {
          return 'tw-rounded-lg'
        }
      }
    })
    const wrapperStateClasses = computed(() => {
      const classes = []

      if (disabledToUse.value) {
        return [
          'tw-bg-grey-300 tw-text-grey-600 tw-border-grey-300 tw-cursor-not-allowed'
        ]
      } else {
        classes.push('tw-bg-white')
      }

      if (hasError.value) {
        return classes.concat(
          'aria-selected:tw-ring-red-400 aria-selected:tw-border-red-400 hover:tw-border-red-400 tw-border-red-400'
        )
      } else if (success.value) {
        return classes.concat(
          'aria-selected:tw-ring-green-500 aria-selected:tw-border-green-500 hover:tw-border-green-500 !tw-border-green-500'
        )
      } else if (warning.value) {
        return classes.concat(
          'aria-selected:tw-ring-amber-500 aria-selected:tw-border-amber-500 hover:tw-border-amber-500 !tw-border-amber-500'
        )
      }

      return classes.concat(
        'hover:tw-border-blue-500 aria-selected:tw-border-blue-500 aria-selected:tw-ring-blue-500 tw-border-grey-300'
      )
    })
    return () => {
      const children = [
        h(
          'div',
          {
            attrs: {
              role: 'group',
              'aria-selected': focused.value.toString()
            },
            staticClass:
              'tw-flex tw-relative tw-flex-wrap tw-items-stretch tw-w-full tw-border tw-border-solid',
            class: [
              wrapperRoundnessClass.value,
              wrapperStateClasses.value,
              wrapperClass.value,
              'tw-transition-colors aria-selected:tw-ring-1'
            ]
          },
          renderContent()
        )
      ]

      if (helperText.value || slots.helperText) {
        if (slots.helperText) {
          children.push(
            h(
              'div',
              { class: helperTextClasses.value },
              addPropertiesToVNode(slots.helperText())
            )
          )
        } else if (helperText.value) {
          const helperTextChildren = []
          if (helperTextIcon.value) {
            helperTextChildren.push(
              h(CIcon, {
                props: { icon: helperTextIcon.value, size: 16 }
              })
            )
          }
          helperTextChildren.push(h('span', helperText.value))

          children.push(
            h('div', { class: helperTextClasses.value }, [
              h(
                'span',
                { staticClass: 'tw-inline-flex tw-items-center tw-gap-1' },
                helperTextChildren
              )
            ])
          )
        }
      }

      return h(
        'div',
        {
          staticClass:
            'tw-flex tw-relative tw-flex-col tw-items-stretch tw-w-full'
        },
        children
      )
    }
  }
})
